import { Box } from '@wix/design-system';
import classNames from 'classnames';
import type { FC } from 'react';
import React, { useContext, useState } from 'react';

import { ModalServiceContext, RicosContext } from './../../../ricos-context';
import { useBiNext } from './../../../_shared/bi-context-provider/bi-context-provider';
import { unreachable } from './../../../editor-common';
import { Panel, PanelHeader } from './../../../ui-components';

import type { SettingsType } from '../../consts';
import { POLL_SETTINGS_MODAL_ID, SETTINGS_TAB } from '../../consts';
import { SETTINGS_MODAL } from '../../data-hooks';
import type { PollPluginEditorConfig } from '../../types';
import { DesignSettingsSection } from './design-settings-section';
import { LayoutSettingsSection } from './layout-settings-section';
import { PollSettingsSection } from './poll-settings-section';
import classes from './settings-modal-scss';
import { TabNavigation } from './tab-navigation';

export type SettingsModalProps = {
  nodeId: string;
  activeTab?: SettingsType;
  config: PollPluginEditorConfig;
  modalId: string;
};

export const SettingsModal: FC<SettingsModalProps> = ({
  nodeId,
  activeTab: initialTab,
  config,
  modalId,
}) => {
  const { t, isMobile } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext);
  const [activeTab, setActiveTab] = useState<SettingsType>(initialTab ?? 'layout');
  const logBi = useBiNext({ pluginId: 'poll', modalId: POLL_SETTINGS_MODAL_ID, nodeId });

  const closeModal = () => modalService.closeModal(modalId);

  return (
    <div
      className={classNames(classes.modal, {
        [classes.modalDesktop]: !isMobile,
      })}
    >
      <Panel
        onCloseButtonClick={closeModal}
        dataHook={SETTINGS_MODAL}
        maxHeight={isMobile ? undefined : 590}
      >
        <PanelHeader title={t('Poll_PollSettings_Common_Header')} />
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />

        <Box
          direction="vertical"
          padding="SP3"
          gap="SP3"
          overflow="auto"
          className={classes.content}
        >
          {(() => {
            switch (activeTab) {
              case SETTINGS_TAB.layout:
                return <LayoutSettingsSection nodeId={nodeId} logBi={logBi} />;
              case SETTINGS_TAB.design:
                return <DesignSettingsSection nodeId={nodeId} logBi={logBi} />;
              case SETTINGS_TAB.settings:
                return <PollSettingsSection nodeId={nodeId} config={config} logBi={logBi} />;
              default:
                unreachable(activeTab);
                return null;
            }
          })()}
        </Box>
      </Panel>
    </div>
  );
};
